/**
 * Emergent Google Auth callback handler.
 *
 * Google sends the user back to the exact URL they came from, appending
 * `#session_id=xxx` in the fragment. We exchange that session_id with our
 * backend (which then calls Emergent's session-data endpoint server-side
 * per the auth playbook - the raw session_id NEVER hits any 3rd party
 * from the browser), receive user + PyJWT + httpOnly cookie, then land
 * the user on the RSVP edit page.
 *
 * Router-level: <App> checks window.location.hash SYNCHRONOUSLY (in
 * render, not useEffect) and mounts this component before any protected
 * route logic runs - see playbook race-condition fix.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePixels } from '@/contexts/PixelContext';
import api from '@/utils/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const { trackEvent } = usePixels();
  // useRef (not useState) so the flag survives StrictMode's double-invoke
  // of useEffect. Set synchronously at the top of the effect.
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing'); // processing | done | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setStatus('error');
      setErrorMsg('Missing session_id in the auth callback URL.');
      return;
    }

    // Preserve claim query params from the original URL so we can pass
    // reg_id + edit_token to the backend for one-shot RSVP linking.
    const searchParams = new URLSearchParams(window.location.search);
    const regId = searchParams.get('reg_id') || '';
    const editToken = searchParams.get('edit_token') || '';

    (async () => {
      try {
        const { data } = await api.post(
          '/auth/google/session',
          { registration_id: regId, edit_token: editToken },
          { headers: { 'X-Session-ID': sessionId } },
        );
        // Store the PyJWT for backwards compat with the existing
        // Bearer-based axios interceptor. The httpOnly cookie was set by
        // the backend response - we can't read it from JS (that's the
        // point of httpOnly).
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
        }
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          updateUser(data.user);
        }

        // Meta Sign Up analytics (Batch 4): fire CompleteRegistration
        // only when the Google session actually PROVISIONED a new user.
        // For returning Google logins, is_new_user=false and we stay
        // silent. content_category='signup' distinguishes this from
        // the RSVP-ticket CompleteRegistration in PaymentSuccessPage.
        if (data.is_new_user === true) {
          try {
            trackEvent('CompleteRegistration', {
              content_name: 'account_signup',
              content_category: 'signup',
              method: 'google',
            });
          } catch (_) { /* never break the flow */ }
        }

        // Strip the session_id from the URL so a reload doesn't try to
        // re-exchange a consumed token.
        try {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        } catch (_) { /* noop */ }

        setStatus('done');

        // Bundle 55 Bug 2 - respect the originating URL. Prior to this
        // bundle, we always shipped the user to /my-events (or the
        // claim edit page) regardless of where they started, so a
        // Google sign-in from the AuthGateModal on an event page
        // dumped them on the profile view instead of resuming the
        // RSVP flow. New logic:
        //   1. If ?next=<path> is present in the URL, use it.
        //   2. If we returned to `/events/:slug*` (that's where the
        //      AuthGateModal lives) and either ?rsvp=1 is set OR the
        //      user is already on an RSVP subroute, stay put - the
        //      EventDetailPage effect reads ?rsvp=1 and drops the
        //      authenticated user into `/events/:slug/rsvp`.
        //   3. Fall back to the historical claim-edit / my-events
        //      routing so nothing else regresses.
        const nextParam = new URLSearchParams(window.location.search).get('next');
        const currentPath = window.location.pathname;
        const isEventContext = /^\/events\/[^/]+/.test(currentPath);
        const hasRsvpFlag = new URLSearchParams(window.location.search).get('rsvp') === '1';

        const claimed = data.claimed_reg_id;

        if (nextParam && nextParam.startsWith('/')) {
          navigate(nextParam, { replace: true });
        } else if (isEventContext && hasRsvpFlag) {
          // Stay on the event page; EventDetailPage's effect will pick
          // up ?rsvp=1 and open the RSVP flow for the freshly signed-in
          // user. Force a full URL reload so the AuthContext state has
          // definitely propagated before EventDetailPage remounts.
          navigate(currentPath + window.location.search, { replace: true });
        } else if (claimed && editToken) {
          navigate(`/rsvp/edit?t=${encodeURIComponent(editToken)}&claimed=1`, { replace: true });
        } else {
          navigate('/my-events', { replace: true });
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || 'Google sign-in failed.';
        setStatus('error');
        setErrorMsg(typeof detail === 'string' ? detail : 'Google sign-in failed.');
      }
    })();
  }, [navigate, updateUser, trackEvent]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-4" data-testid="auth-callback">
        {status === 'processing' && (
          <>
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-secondary-700" />
            <div className="font-display text-xl font-semibold text-primary-900">Signing you in...</div>
            <div className="text-sm text-neutral-600">Just a moment while we save your account.</div>
          </>
        )}
        {status === 'done' && (
          <>
            <CheckCircle2 className="h-10 w-10 mx-auto text-green-600" />
            <div className="font-display text-xl font-semibold text-primary-900">You&apos;re in.</div>
            <div className="text-sm text-neutral-600">Redirecting...</div>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="h-10 w-10 mx-auto text-accent-700" />
            <div className="font-display text-xl font-semibold text-primary-900">Sign-in failed</div>
            <div className="text-sm text-neutral-600" data-testid="auth-callback-error">{errorMsg}</div>
            <button
              className="mt-4 text-sm text-secondary-700 hover:text-secondary-800 underline"
              onClick={() => navigate('/', { replace: true })}
            >
              Return home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
